import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  DpeState,
  PropertyAddMediaDto,
  PropertyCharacteristicsDto,
  PropertyCommonAreaType,
  PropertyCondition,
  PropertyConstructionDto,
  PropertyConstructionMaterial,
  PropertyConstructionPeriod,
  PropertyCoOwnershipProcedureType,
  PropertyCountry,
  PropertyCreationStep,
  PropertyCurrentMarketingMode,
  PropertyDefinitionDto,
  PropertyDpeDto,
  PropertyDto,
  PropertyEquipment,
  PropertyExposure,
  PropertyFinancialsDto,
  PropertyGeolocationType,
  PropertyKeyInfoDto,
  PropertyKeyManagement,
  PropertyLocationDto,
  PropertyMandateDto,
  PropertyMandateDuration,
  PropertyMandateType,
  PropertyOfferType,
  PropertyOrientation,
  PropertyParkingSubType,
  PropertyPhotoDto,
  PropertyPresentationDto,
  PropertyResidenceType,
  PropertyRoofMaterial,
  PropertyRoomType,
  PropertySaleUrgency,
  PropertySellerBeneficiaryDto,
  PropertySellerDto,
  PropertySellerPriority,
  PropertySellerRole,
  PropertySpaceDetailDto,
  PropertyStanding,
  PropertySubtype,
  PropertyType,
  PropertyView,
} from '@workspace/data-types';
import {
  PropertyAccessibilityForm,
  PropertyAddressForm,
  PropertyApartmentAccessibilityForm,
  PropertyAvailabilityForm,
  PropertyCharacteristicsForm,
  PropertyConditionsForm,
  PropertyConstructionForm,
  PropertyConstructionMaterialForm,
  PropertyCoordinatesForm,
  PropertyCoOwnershipForm,
  PropertyDefinitionForm,
  PropertyDpeForm,
  PropertyEquipmentsForm,
  PropertyExteriorForm,
  PropertyFinancialsForm,
  PropertyForm,
  PropertyInsulationForm,
  PropertyInteriorForm,
  PropertyKeyInfoForm,
  PropertyLocationForm,
  PropertyMandateForm,
  PropertyMediaForm,
  PropertyParkingForm,
  PropertyPresentationForm,
  PropertySellerBeneficiaryForm,
  PropertySellerForm,
  PropertySpaceForm,
  PropertySpaceInCoOwnershipForm,
  PropertySurfaceForm,
  PropertyTaxesAndFeesForm,
  PropertyTrusteeForm,
} from '../types/property-form.types';

export class PropertyFormBuilder {
  /**
   * Builds a complete PropertyDto form group.
   */
  static buildPropertyForm(fb: FormBuilder, dto?: PropertyDto | null): FormGroup<PropertyForm> {
    return fb.group({
      definition: this.buildDefinitionGroup(fb, dto?.definition),
      keyInfo: this.buildKeyInfoGroup(fb, dto?.keyInfo),
      characteristics: this.buildCharacteristicsGroup(fb, dto?.characteristics),
      construction: this.buildConstructionGroup(fb, dto?.construction),
      location: this.buildLocationGroup(fb, dto?.location),
      dpe: this.buildDpeGroup(fb, dto?.dpe),
      presentation: this.buildPresentationGroup(fb, dto?.presentation),
      financials: this.buildFinancialsGroup(fb, dto?.financials),
      mandate: this.buildMandateGroup(fb, dto?.mandate),
    });
  }

  /**
   * Builds the presentation part of the form.
   */
  static buildPresentationGroup(
    fb: FormBuilder,
    dto?: PropertyPresentationDto | null,
  ): FormGroup<PropertyPresentationForm> {
    return fb.group({
      title: new FormControl<string | null>(dto?.title || '', [Validators.required]),
      description: new FormControl<string | null>(dto?.description || '', [Validators.required]),
    });
  }

  static buildMediaGroup(fb: FormBuilder, dto?: PropertyAddMediaDto | null): FormGroup<PropertyMediaForm> {
    return fb.group({
      photos: new FormControl<PropertyPhotoDto[] | null>(dto?.photos || [], [
        Validators.required,
        Validators.minLength(1),
      ]),
    });
  }

  static buildFinancialsGroup(
    fb: FormBuilder,
    dto?: PropertyFinancialsDto | null,
  ): FormGroup<PropertyFinancialsForm> {
    return fb.group({
      sellingPriceCents: new FormControl<number | null>(dto?.sellingPriceCents || null, [
        Validators.required,
      ]),
      chargesCents: new FormControl<number | null>(dto?.chargesCents || null),
      coOwnershipChargesCents: new FormControl<number | null>(dto?.coOwnershipChargesCents || null),
      precisions: new FormControl<string | null>(dto?.precisions || ''),
      taxesAndFees: fb.group<PropertyTaxesAndFeesForm>({
        notaryFeesCents: new FormControl<number | null>(dto?.taxesAndFees?.notaryFeesCents || null),
        propertyTaxCents: new FormControl<number | null>(dto?.taxesAndFees?.propertyTaxCents || null),
        housingTaxCents: new FormControl<number | null>(dto?.taxesAndFees?.housingTaxCents || null),
        wasteCollectionTaxCents: new FormControl<number | null>(
          dto?.taxesAndFees?.wasteCollectionTaxCents || null,
        ),
      }),
    });
  }

  /**
   * Builds the mandate & seller expectations part of the form.
   */
  static buildMandateGroup(fb: FormBuilder, dto?: PropertyMandateDto | null): FormGroup<PropertyMandateForm> {
    return fb.group({
      type: new FormControl<PropertyMandateType | null>(dto?.type ?? null, [Validators.required]),
      duration: new FormControl<PropertyMandateDuration | null>(
        dto?.duration || PropertyMandateDuration.THREE_MONTHS,
        [Validators.required],
      ),
      targetCommissionRate: new FormControl<number | null>(dto?.targetCommissionRate ?? 3.5, [
        Validators.min(0.5),
        Validators.max(15),
      ]),
      keyManagement: new FormControl<PropertyKeyManagement[] | null>(
        dto?.keyManagement
          ? Array.isArray(dto.keyManagement)
            ? dto.keyManagement
            : [dto.keyManagement as unknown as PropertyKeyManagement]
          : [],
      ),
      priorities: new FormControl<PropertySellerPriority[] | null>(dto?.priorities || []),
      specialInstructions: new FormControl<string | null>(dto?.specialInstructions || ''),
    });
  }

  /**
   * Builds the location part of the form.
   */
  static buildLocationGroup(
    fb: FormBuilder,
    dto?: PropertyLocationDto | null,
  ): FormGroup<PropertyLocationForm> {
    return fb.group({
      address: fb.group<PropertyAddressForm>({
        street: new FormControl<string | null>(dto?.address?.street || '', [Validators.required]),
        city: new FormControl<string | null>(dto?.address?.city || ''),
        zipCode: new FormControl<string | null>(dto?.address?.zipCode || ''),
        country: new FormControl<PropertyCountry | null>(dto?.address?.country || PropertyCountry.FR),
        complement: new FormControl<string | null>(dto?.address?.complement || ''),
        district: new FormControl<string | null>(dto?.address?.district || ''),
        houseNumber: new FormControl<string | null>(dto?.address?.houseNumber || ''),
        label: new FormControl<string | null>(dto?.address?.label || ''),
        region: new FormControl<string | null>(dto?.address?.region || ''),
        cityCode: new FormControl<string | null>(dto?.address?.cityCode || ''),
      }),
      coordinates: fb.group<PropertyCoordinatesForm>({
        latitude: new FormControl<number | null>(dto?.coordinates?.latitude || null, [Validators.required]),
        longitude: new FormControl<number | null>(dto?.coordinates?.longitude || null, [Validators.required]),
      }),
      geolocationType: new FormControl<PropertyGeolocationType | null>(
        dto?.geolocationType || PropertyGeolocationType.NEIGHBORHOOD,
      ),
    });
  }

  /**
   * Builds the DPE part of the form.
   */
  static buildDpeGroup(fb: FormBuilder, dto?: PropertyDpeDto | null): FormGroup<PropertyDpeForm> {
    return fb.group({
      state: new FormControl<string | null>(dto?.state || null, [Validators.required]),
      energyClass: new FormControl<string | null>(dto?.energyClass || null),
      climateClass: new FormControl<string | null>(dto?.climateClass || null),
      energyConsumption: new FormControl<number | null>(dto?.energyConsumption || null),
      greenhouseGasEmission: new FormControl<number | null>(dto?.greenhouseGasEmission || null),
      finalEnergyConsumption: new FormControl<number | null>(dto?.finalEnergyConsumption || null),
      estimatedCostMin: new FormControl<number | null>(dto?.estimatedCostMin || null),
      estimatedCostMax: new FormControl<number | null>(dto?.estimatedCostMax || null),
      priceIndexingDate: new FormControl<Date | null>(dto?.priceIndexingDate || null),
      realizationDate: new FormControl<Date | null>(dto?.realizationDate || null),
      ademeNumber: new FormControl<string | null>(dto?.ademeNumber || ''),
    }) as FormGroup<PropertyDpeForm>;
  }

  /**
   * Applies conditional validators to DPE group based on state.
   */
  static applyDpeConditionalValidators(group: FormGroup<PropertyDpeForm>): void {
    const state = group.get('state')?.value;
    const fields: (keyof PropertyDpeForm)[] = [
      'energyConsumption',
      'greenhouseGasEmission',
      'realizationDate',
      'finalEnergyConsumption',
      'energyClass',
      'climateClass',
    ];

    if (state === DpeState.DONE) {
      fields.forEach((f) => {
        group.get(f)?.setValidators([Validators.required]);
        group.get(f)?.updateValueAndValidity({ emitEvent: false });
      });
      group
        .get('ademeNumber')
        ?.setValidators([Validators.required, Validators.pattern(/^[0-9]{4}[A-Z][0-9]{7}[A-Z]$/)]);
      group.get('ademeNumber')?.updateValueAndValidity({ emitEvent: false });
      group.get('estimatedCostMin')?.setValidators([Validators.required, Validators.min(1)]);
      group.get('estimatedCostMin')?.updateValueAndValidity({ emitEvent: false });
      group.get('estimatedCostMax')?.setValidators([Validators.required, Validators.min(1)]);
      group.get('estimatedCostMax')?.updateValueAndValidity({ emitEvent: false });

      const minVal = group.get('estimatedCostMin')?.value;
      const maxVal = group.get('estimatedCostMax')?.value;
      if (
        minVal !== null &&
        minVal !== undefined &&
        maxVal !== null &&
        maxVal !== undefined &&
        maxVal < minVal
      ) {
        group.get('estimatedCostMax')?.setErrors({ invalidCostRange: true });
      }
    } else {
      fields.forEach((f) => {
        group.get(f)?.clearValidators();
        group.get(f)?.updateValueAndValidity({ emitEvent: false });
      });
      group.get('ademeNumber')?.clearValidators();
      group.get('ademeNumber')?.updateValueAndValidity({ emitEvent: false });
      group.get('estimatedCostMin')?.clearValidators();
      group.get('estimatedCostMin')?.updateValueAndValidity({ emitEvent: false });
      group.get('estimatedCostMax')?.clearValidators();
      group.get('estimatedCostMax')?.updateValueAndValidity({ emitEvent: false });
    }
  }

  /**
   * Applies conditional validators to Availability group.
   */
  static applyAvailabilityConditionalValidators(group: FormGroup<PropertyAvailabilityForm>): void {
    const isAlreadyOnSale = group.get('isAlreadyOnSale')?.value;
    const currentControl = group.get('currentMarketingMode');
    const exclusiveEndDateControl = group.get('exclusiveMandateEndDate');

    if (isAlreadyOnSale === true) {
      currentControl?.setValidators([Validators.required]);

      if (currentControl?.value === PropertyCurrentMarketingMode.AGENCY_EXCLUSIVE) {
        exclusiveEndDateControl?.setValidators([Validators.required]);
      } else {
        exclusiveEndDateControl?.clearValidators();
        exclusiveEndDateControl?.setValue(null);
      }
    } else {
      currentControl?.clearValidators();
      exclusiveEndDateControl?.clearValidators();
      currentControl?.setValue(null);
      exclusiveEndDateControl?.setValue(null);
    }

    const isOccupied = group.get('isOccupied')?.value;
    const occupiedUntilControl = group.get('occupiedUntil');

    if (isOccupied) {
      occupiedUntilControl?.setValidators([Validators.required]);
    } else {
      occupiedUntilControl?.clearValidators();
      occupiedUntilControl?.setValue(null);
    }

    currentControl?.updateValueAndValidity({ emitEvent: false });
    exclusiveEndDateControl?.updateValueAndValidity({ emitEvent: false });
    occupiedUntilControl?.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Applies conditional validators to CoOwnership group.
   * If the property is an apartment, isInCoOwnership is mandatory.
   * If isInCoOwnership is true, numberOfLots becomes mandatory.
   */
  static applyCoOwnershipConditionalValidators(
    group: FormGroup<PropertyCoOwnershipForm>,
    propertyType?: PropertyType,
  ): void {
    const isApartment = propertyType === PropertyType.APARTMENT;
    const isInCoOwnershipControl = group.get('isInCoOwnership');
    const numberOfLotsControl = group.get('numberOfLots');
    const lotNumberControl = group.get('lotNumber');
    const trusteeNameControl = group.get('trustee.name');

    if (isApartment) {
      isInCoOwnershipControl?.setValidators([Validators.required]);
    } else {
      isInCoOwnershipControl?.clearValidators();
    }
    isInCoOwnershipControl?.updateValueAndValidity({ emitEvent: false });

    const updateConditionals = (isInCo: boolean | null) => {
      if (isInCo === true) {
        numberOfLotsControl?.setValidators([Validators.required, Validators.min(2)]);
        lotNumberControl?.clearValidators();
        trusteeNameControl?.clearValidators();
      } else {
        numberOfLotsControl?.clearValidators();
        lotNumberControl?.clearValidators();
        trusteeNameControl?.clearValidators();
      }
      numberOfLotsControl?.updateValueAndValidity({ emitEvent: false });
      lotNumberControl?.updateValueAndValidity({ emitEvent: false });
      trusteeNameControl?.updateValueAndValidity({ emitEvent: false });
    };

    updateConditionals(isInCoOwnershipControl?.value ?? null);
  }

  /**
   * Builds the beneficiary part of the seller form.
   */
  static buildSellerBeneficiaryGroup(
    fb: FormBuilder,
    dto?: PropertySellerBeneficiaryDto | null,
  ): FormGroup<PropertySellerBeneficiaryForm> {
    return fb.group<PropertySellerBeneficiaryForm>({
      firstName: new FormControl<string | null>(dto?.firstName || ''),
      lastName: new FormControl<string | null>(dto?.lastName || ''),
      email: new FormControl<string | null>(dto?.email || '', [Validators.email]),
      phone: new FormControl<string | null>(dto?.phone || ''),
      companyName: new FormControl<string | null>(dto?.companyName || ''),
      siren: new FormControl<string | null>(dto?.siren || ''),
      estateName: new FormControl<string | null>(dto?.estateName || ''),
      notaryName: new FormControl<string | null>(dto?.notaryName || ''),
    });
  }

  /**
   * Builds the seller part of the form.
   */
  static buildSellerGroup(fb: FormBuilder, dto?: PropertySellerDto | null): FormGroup<PropertySellerForm> {
    return fb.group<PropertySellerForm>({
      role: new FormControl<PropertySellerRole | null>(dto?.role || PropertySellerRole.OWNER, [
        Validators.required,
      ]),
      beneficiary: this.buildSellerBeneficiaryGroup(fb, dto?.beneficiary),
      representationDetails: new FormControl<string | null>(dto?.representationDetails || ''),
      hasOwnerConsent: new FormControl<boolean | null>(dto?.hasOwnerConsent ?? true),
    });
  }

  /**
   * Applies conditional validators to Seller group.
   */
  static applySellerConditionalValidators(group: FormGroup<PropertySellerForm>): void {
    const role = group.get('role')?.value;
    const beneficiaryGroup = group.controls.beneficiary;
    const firstName = beneficiaryGroup.get('firstName');
    const lastName = beneficiaryGroup.get('lastName');
    const companyName = beneficiaryGroup.get('companyName');
    const estateName = beneficiaryGroup.get('estateName');
    const hasOwnerConsent = group.get('hasOwnerConsent');

    // Clear all validators first
    firstName?.clearValidators();
    lastName?.clearValidators();
    companyName?.clearValidators();
    estateName?.clearValidators();
    hasOwnerConsent?.clearValidators();

    if (role === PropertySellerRole.FAMILY) {
      firstName?.setValidators([Validators.required]);
      lastName?.setValidators([Validators.required]);
      hasOwnerConsent?.setValidators([Validators.requiredTrue]);
    } else if (role === PropertySellerRole.COMPANY) {
      companyName?.setValidators([Validators.required]);
      hasOwnerConsent?.setValidators([Validators.requiredTrue]);
    } else if (role === PropertySellerRole.ESTATE_HEIR) {
      estateName?.setValidators([Validators.required]);
      hasOwnerConsent?.setValidators([Validators.requiredTrue]);
    }

    firstName?.updateValueAndValidity({ emitEvent: false });
    lastName?.updateValueAndValidity({ emitEvent: false });
    companyName?.updateValueAndValidity({ emitEvent: false });
    estateName?.updateValueAndValidity({ emitEvent: false });
    hasOwnerConsent?.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Builds the definition part of the form.
   */
  static buildDefinitionGroup(
    fb: FormBuilder,
    dto?: PropertyDefinitionDto | null,
  ): FormGroup<PropertyDefinitionForm> {
    const sellerGroup = this.buildSellerGroup(fb, dto?.seller);
    this.applySellerConditionalValidators(sellerGroup);

    return fb.group<PropertyDefinitionForm>({
      offerType: new FormControl<PropertyOfferType | null>(dto?.offerType || null, [Validators.required]),
      propertyType: new FormControl<PropertyType | null>(dto?.propertyType || null, [Validators.required]),
      residenceType: new FormControl<PropertyResidenceType | null>(dto?.residenceType || null, [
        Validators.required,
      ]),
      seller: sellerGroup,
    });
  }

  /**
   * Builds the key info part of the form.
   */
  static buildKeyInfoGroup(
    fb: FormBuilder,
    dto?: PropertyKeyInfoDto | null,
    propertyType?: PropertyType | null,
  ): FormGroup<PropertyKeyInfoForm> {
    const isHouse = propertyType === PropertyType.HOUSE;
    const landValidators = isHouse ? [Validators.required, Validators.min(1)] : [];

    return fb.group({
      subtype: new FormControl<PropertySubtype | null>(dto?.subtype || null, [Validators.required]),
      surface: fb.group<PropertySurfaceForm>({
        habitable: new FormControl<number | null>(dto?.surface?.habitable || null, [
          Validators.required,
          Validators.min(9),
        ]),
        ground: new FormControl<number | null>(dto?.surface?.ground || null),
        land: new FormControl<number | null>(dto?.surface?.land || null, landValidators),
      }),
      availability: fb.group<PropertyAvailabilityForm>({
        isOccupied: new FormControl<boolean>(dto?.availability?.isOccupied || false, {
          nonNullable: true,
        }),
        occupiedUntil: new FormControl<Date | null>(dto?.availability?.occupiedUntil || null),
        saleUrgency: new FormControl<PropertySaleUrgency | null>(dto?.availability?.saleUrgency || null, [
          Validators.required,
        ]),
        isAlreadyOnSale: new FormControl<boolean | null>(dto?.availability?.isAlreadyOnSale ?? null, [
          Validators.required,
        ]),
        currentMarketingMode: new FormControl<PropertyCurrentMarketingMode | null>(
          dto?.availability?.currentMarketingMode || null,
        ),
        exclusiveMandateEndDate: new FormControl<Date | null>(
          dto?.availability?.exclusiveMandateEndDate
            ? new Date(dto.availability.exclusiveMandateEndDate)
            : null,
        ),
      }),
      accessibility: fb.group<PropertyAccessibilityForm>({
        apartment: fb.group<PropertyApartmentAccessibilityForm>({
          floor: new FormControl<number | null>(dto?.accessibility?.apartment?.floor || null),
          numberOfFloors: new FormControl<number | null>(
            dto?.accessibility?.apartment?.numberOfFloors || null,
          ),
        }),
        buildingNumberOfFloors: new FormControl<number | null>(
          dto?.accessibility?.buildingNumberOfFloors || null,
        ),
        buildingHasElevator: new FormControl<boolean>(dto?.accessibility?.buildingHasElevator || false, {
          nonNullable: true,
        }),
        pmrCompliance: new FormControl<boolean>(dto?.accessibility?.pmrCompliance || false, {
          nonNullable: true,
        }),
      }),
      coOwnership: fb.group<PropertyCoOwnershipForm>({
        isInCoOwnership: new FormControl<boolean | null>(
          dto?.coOwnership?.isInCoOwnership ?? (propertyType === PropertyType.APARTMENT ? null : false),
        ),
        numberOfLots: new FormControl<number | null>(dto?.coOwnership?.numberOfLots ?? null),
        lotNumber: new FormControl<string | null>(dto?.coOwnership?.lotNumber || ''),
        trustee: fb.group<PropertyTrusteeForm>({
          name: new FormControl<string | null>(dto?.coOwnership?.trustee?.name || ''),
          isOnline: new FormControl<boolean>(dto?.coOwnership?.trustee?.isOnline || false, {
            nonNullable: true,
          }),
          hasOngoingProcedures: new FormControl<boolean>(
            dto?.coOwnership?.trustee?.hasOngoingProcedures || false,
            {
              nonNullable: true,
            },
          ),
          ongoingProcedures: new FormControl<PropertyCoOwnershipProcedureType[] | null>(
            dto?.coOwnership?.trustee?.ongoingProcedures || [],
          ),
        }),
      }),
    });
  }

  /**
   * Builds the complete characteristics part of the form.
   */
  static buildCharacteristicsGroup(
    fb: FormBuilder,
    dto?: PropertyCharacteristicsDto | null,
  ): FormGroup<PropertyCharacteristicsForm> {
    return fb.group({
      interior: fb.group<PropertyInteriorForm>({
        rooms: fb.array(dto?.interior?.rooms?.map((r) => this.buildSpaceGroup(fb, r, true)) || []),
        bedroom: fb.array(dto?.interior?.bedroom?.map((r) => this.buildSpaceGroup(fb, r, false)) || []),
        bathroom: fb.array(dto?.interior?.bathroom?.map((r) => this.buildSpaceGroup(fb, r, false)) || []),
        showerRoom: fb.array(dto?.interior?.showerRoom?.map((r) => this.buildSpaceGroup(fb, r, false)) || []),
        wc: new FormControl<number | null>(dto?.interior?.wc || 0),
      }),
      exterior: fb.group<PropertyExteriorForm>({
        loggia: fb.array(dto?.exterior?.loggia?.map((r) => this.buildSpaceInCoOwnershipGroup(fb, r)) || []),
        terrace: fb.array(dto?.exterior?.terrace?.map((r) => this.buildSpaceInCoOwnershipGroup(fb, r)) || []),
        balcony: fb.array(dto?.exterior?.balcony?.map((r) => this.buildSpaceInCoOwnershipGroup(fb, r)) || []),
        garden: fb.array(dto?.exterior?.garden?.map((r) => this.buildSpaceInCoOwnershipGroup(fb, r)) || []),
        swimmingPool: fb.array(
          dto?.exterior?.swimmingPool?.map((r) => this.buildSpaceInCoOwnershipGroup(fb, r)) || [],
        ),
        cellar: fb.array(dto?.exterior?.cellar?.map((r) => this.buildSpaceInCoOwnershipGroup(fb, r)) || []),
      }),
      parkings: fb.array(
        dto?.parkings?.map((p) =>
          fb.group<PropertyParkingForm>({
            type: new FormControl<PropertyParkingSubType | null>(p.type || null),
            count: new FormControl<number | null>(p.count || null),
            totalSurface: new FormControl<number | null>(p.totalSurface || null),
            lotNumber: new FormControl<string | null>(p.lotNumber || ''),
          }),
        ) || [],
      ),
      parkingAvailable: new FormControl<boolean>(!!dto?.parkings?.length, {
        nonNullable: true,
      }),
      equipments: fb.group<PropertyEquipmentsForm>({
        items: new FormControl<PropertyEquipment[] | null>(dto?.equipments?.items || []),
      }),
      commonAreas: fb.array(
        dto?.commonAreas?.map((ca) =>
          this.buildSpaceGroup(fb, ca as unknown as PropertySpaceDetailDto, true),
        ) || [],
      ),
    });
  }

  /**
   * Builds the construction part of the form.
   */
  static buildConstructionGroup(
    fb: FormBuilder,
    dto?: PropertyConstructionDto | null,
    propertyType?: PropertyType | string | null,
  ): FormGroup<PropertyConstructionForm> {
    const isHouse = propertyType === PropertyType.HOUSE || propertyType === 'house';
    return fb.group<PropertyConstructionForm>({
      year: new FormControl<PropertyConstructionPeriod | null>(dto?.year || null, [Validators.required]),
      material: fb.group<PropertyConstructionMaterialForm>({
        walls: new FormControl<PropertyConstructionMaterial[] | null>(dto?.material?.walls || []),
        roof: new FormControl<PropertyRoofMaterial[] | null>(dto?.material?.roof || []),
      }),
      buildingHeight: new FormControl<number | null>(dto?.buildingHeight ?? null),
      groundFootprint: new FormControl<number | null>(dto?.groundFootprint ?? null),
      ceilingHeight: new FormControl<number | null>(dto?.ceilingHeight ?? null),
      insulation: fb.group<PropertyInsulationForm>({
        walls: new FormControl<string | null>(dto?.insulation?.walls || null),
        roof: new FormControl<string | null>(dto?.insulation?.roof || null),
        floor: new FormControl<string | null>(dto?.insulation?.floor || null),
        glazing: new FormControl<string | null>(dto?.insulation?.glazing || null),
        envelope: new FormControl<string | null>(dto?.insulation?.envelope || null),
      }),
      openings: new FormControl<number | null>(dto?.openings || null),
      orientation: new FormControl<PropertyOrientation[] | null>(dto?.orientation || []),
      standing: new FormControl<PropertyStanding | null>(dto?.standing || null, [Validators.required]),
      view: new FormControl<PropertyView[] | null>(
        Array.isArray(dto?.view) ? dto.view : dto?.view ? [dto.view] : [],
      ),
      exposure: new FormControl<PropertyExposure | null>(dto?.exposure || null),
      precision: new FormControl<string | null>(dto?.precision || ''),
      conditions: fb.group<PropertyConditionsForm>({
        interior: new FormControl<PropertyCondition | null>(dto?.conditions?.interior || null, [
          Validators.required,
        ]),
        commonAreas: new FormControl<PropertyCondition | null>(
          dto?.conditions?.commonAreas || null,
          isHouse ? [] : [Validators.required],
        ),
      }),
    });
  }

  /**
   * Helper to build a space detail group.
   */
  static buildSpaceInCoOwnershipGroup(
    fb: FormBuilder,
    dto?: PropertySpaceDetailDto | null,
    requiresType = false,
  ): FormGroup<PropertySpaceInCoOwnershipForm> {
    return fb.group({
      type: new FormControl<PropertyRoomType | PropertyCommonAreaType | null>(
        dto?.type || null,
        requiresType ? [Validators.required] : [],
      ),
      surface: new FormControl<number | null>(dto?.surface || null, [
        Validators.required,
        Validators.min(0.1),
      ]),
      precision: new FormControl<string | null>(dto?.precision || ''),
      lotNumber: new FormControl<string | null>(dto?.lotNumber || ''),
    });
  }

  /**
   * Helper to build a space detail group.
   */
  static buildSpaceGroup(
    fb: FormBuilder,
    dto?: PropertySpaceDetailDto | null,
    requiresType = false,
  ): FormGroup<PropertySpaceForm> {
    return fb.group({
      type: new FormControl<PropertyRoomType | PropertyCommonAreaType | null>(
        dto?.type || null,
        requiresType ? [Validators.required] : [],
      ),
      surface: new FormControl<number | null>(dto?.surface || null, [
        Validators.required,
        Validators.min(0.1),
      ]),
      precision: new FormControl<string | null>(dto?.precision || ''),
    });
  }

  /**
   * Checks if a specific step is valid (completed) for a given DTO.
   */
  static isStepValid(step: PropertyCreationStep, prop: PropertyDto, fb: FormBuilder): boolean {
    let group: FormGroup | null;

    switch (step) {
      case PropertyCreationStep.DEFINITION:
        group = this.buildDefinitionGroup(fb, prop.definition);
        break;
      case PropertyCreationStep.LOCATION:
        group = this.buildLocationGroup(fb, prop.location);
        break;
      case PropertyCreationStep.CADASTRAL:
        return Boolean(prop.geospatial?.cadastral?.parcels?.length);
      case PropertyCreationStep.KEY_INFOS:
        group = this.buildKeyInfoGroup(fb, prop.keyInfo, prop.definition?.propertyType);
        this.applyAvailabilityConditionalValidators(group.get('availability') as FormGroup);
        this.applyCoOwnershipConditionalValidators(
          group.get('coOwnership') as FormGroup<PropertyCoOwnershipForm>,
          prop.definition?.propertyType,
        );
        break;
      case PropertyCreationStep.DPE:
        group = this.buildDpeGroup(fb, prop.dpe);
        this.applyDpeConditionalValidators(group as FormGroup<PropertyDpeForm>);
        break;
      case PropertyCreationStep.PRESENTATION:
        group = this.buildPresentationGroup(fb, prop.presentation);
        break;
      case PropertyCreationStep.FINANCIALS:
        group = this.buildFinancialsGroup(fb, prop.financials);
        break;
      case PropertyCreationStep.CONSTRUCTION:
        group = this.buildConstructionGroup(fb, prop.construction, prop.definition?.propertyType);
        break;
      case PropertyCreationStep.PHOTOS:
        group = this.buildMediaGroup(fb, { photos: prop.media?.photos || [] });
        break;
      // Optional steps or steps with no specific requirement beyond "reached"
      case PropertyCreationStep.CHARACTERISTICS:
      case PropertyCreationStep.GEOHAZARDS:
        return false;
      default:
        return true;
    }

    if (group) {
      group.updateValueAndValidity();
      return group.valid;
    }

    return true;
  }
}
